import React from 'react';

type SharePreviewStatus = 'idle' | 'loading' | 'ready' | 'error';

type SlapPublishSharePanelProps = {
  slapId: number | null;
  sharePreviewStatus: SharePreviewStatus;
  shareCheckCount: number;
  shareNextCheckIn: number | null;
  shareResourceUrl: string | null;
  sharePreviewUrl: string | null;
  sharePreviewDisplayUrl: string | null;
  stickerName: string | null;
  publisherEns: string | null;
  shareComposerBusy: boolean;
  isMiniApp: boolean;
  hasTimedOut?: boolean;
  onShareX: () => void;
  onShareFarcaster: () => void;
  onClose?: () => void;
};

export default function SlapPublishSharePanel({
  slapId,
  sharePreviewStatus,
  shareCheckCount,
  shareNextCheckIn,
  shareResourceUrl,
  sharePreviewUrl,
  sharePreviewDisplayUrl,
  stickerName,
  publisherEns,
  shareComposerBusy,
  isMiniApp,
  hasTimedOut = false,
  onShareX,
  onShareFarcaster,
  onClose,
}: SlapPublishSharePanelProps) {
  if (!slapId) {
    return null;
  }

  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-left">
      <div className="mt-1 mb-2 font-semibold text-slate-800">StickerChain Slap #{slapId}</div>
      {sharePreviewStatus === 'loading' && (
        <div className="text-sm text-slate-600">
          <div>Please wait while your slap is rendered...</div>
        </div>
      )}
      {sharePreviewStatus === 'error' && (
        <p className="text-sm text-slate-600">
          {hasTimedOut
            ? 'It is taking longer than expected to generate the preview image. Please check again later.'
            : 'A shareable preview image is being generated.'}
        </p>
      )}
      {hasTimedOut && onClose && (
        <button
          type="button"
          onClick={onClose}
          className="mt-3 w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
        >
          Close
        </button>
      )}
      {(sharePreviewStatus === 'loading' || sharePreviewStatus === 'error') &&
        shareResourceUrl &&
        !hasTimedOut && (
        <div className="mt-2 text-xs text-slate-500">
          <div>• Check #{shareCheckCount}</div>
          <div className="break-all">• {shareResourceUrl}</div>
          {shareNextCheckIn !== null && (
            <div>
              • {shareNextCheckIn === 0 ? 'checking...' : `next check in ${shareNextCheckIn}s`}
            </div>
          )}
        </div>
      )}
      {sharePreviewStatus === 'ready' && (
        <>
          {(sharePreviewDisplayUrl || sharePreviewUrl) && (
            <div className="mb-4">
              <img
                src={sharePreviewDisplayUrl || sharePreviewUrl || undefined}
                alt={`Slap ${slapId} share preview`}
                className="w-full rounded-xl border border-slate-200"
              />
            </div>
          )}
          <p className="mt-2 py-1 text-sm text-slate-600">
            {publisherEns && stickerName
              ? `Support "${stickerName}" from ${publisherEns} by sharing your slap.`
              : `Support ${
                  stickerName ? `"${stickerName}"` : 'this sticker'
                } by sharing your slap.`}
          </p>
          {isMiniApp ? (
            <button
              type="button"
              onClick={onShareFarcaster}
              disabled={shareComposerBusy}
              className="mt-3 w-full rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              Share on Farcaster
            </button>
          ) : (
            <div className="mt-3 flex flex-col gap-2">
              <button
                type="button"
                onClick={onShareX}
                className="w-full rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 transition"
              >
                Share on X
              </button>
              <button
                type="button"
                onClick={onShareFarcaster}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-100 transition"
              >
                Share on Farcaster
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
